import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import type {
  CollectionAttemptActorTypeValue,
  CollectionAttemptEventDTO,
  CollectionAttemptEventTypeValue,
  CollectionAttemptStatusValue
} from '../../core/models';

const EVENT_LABELS: Record<CollectionAttemptEventTypeValue, string> = {
  CREATED: 'Cobro solicitado',
  PROVIDER_REQUESTED: 'Enviado al proveedor',
  PROVIDER_RESPONDED: 'Respuesta del proveedor',
  PROVIDER_NOTIFIED: 'Aviso de la liga de pago',
  MANUAL_RECORDED: 'Pago manual registrado',
  MANUAL_UPDATED: 'Pago manual editado',
  PAYMENT_CANCELLED: 'Pago cancelado',
  RESOLVED_MANUALLY: 'Resuelto manualmente',
  MARKED_STALE: 'Sin respuesta del proveedor; pasa a revisión',
  MATERIALIZED: 'Venta y pago registrados',
  MATERIALIZATION_FAILED: 'Falló el registro de la venta',
  VOID_REQUESTED: 'Cancelación solicitada',
  VOID_RESPONDED: 'Respuesta a la cancelación',
  VOIDED: 'Cargo de validación cancelado',
  VOID_FAILED: 'Cancelación fallida',
  MIGRATED: 'Migrado del registro anterior'
};

const EVENT_ICONS: Partial<Record<CollectionAttemptEventTypeValue, string>> = {
  CREATED: 'pi-send',
  PROVIDER_REQUESTED: 'pi-send',
  PROVIDER_RESPONDED: 'pi-server',
  PROVIDER_NOTIFIED: 'pi-bell',
  MANUAL_RECORDED: 'pi-user-edit',
  MANUAL_UPDATED: 'pi-pencil',
  PAYMENT_CANCELLED: 'pi-times-circle',
  RESOLVED_MANUALLY: 'pi-check-square',
  MARKED_STALE: 'pi-clock',
  MATERIALIZED: 'pi-file-check',
  MATERIALIZATION_FAILED: 'pi-exclamation-triangle',
  VOID_REQUESTED: 'pi-undo',
  VOID_RESPONDED: 'pi-server',
  VOIDED: 'pi-undo',
  VOID_FAILED: 'pi-exclamation-triangle',
  MIGRATED: 'pi-database'
};

const STATUS_LABELS: Record<CollectionAttemptStatusValue, string> = {
  PROCESSING: 'En proceso',
  APPROVED: 'Aprobado',
  DECLINED: 'Declinado',
  REJECTED: 'Rechazado',
  PENDING_RECONCILIATION: 'En revisión',
  VOID_PENDING: 'Cancelación pendiente',
  VOIDED: 'Cancelado',
  VOID_FAILED: 'Cancelación fallida',
  PENDING: 'Pendiente',
  EXPIRED: 'Expirado',
  CANCELLED: 'Cancelado'
};

const ACTOR_LABELS: Record<CollectionAttemptActorTypeValue, string> = {
  SYSTEM: 'Sistema',
  USER: 'Usuario',
  PROVIDER: 'Proveedor',
  MIGRATION: 'Migración'
};

/** Historial de un intento de cobro en orden cronológico. Los datos los carga la pantalla que lo usa. */
@Component({
  selector: 'app-collection-attempt-timeline',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (loading()) {
      <div class="flex items-center gap-2 py-2 text-xs text-slate-500">
        <i class="pi pi-spin pi-spinner text-sky-500"></i>
        Cargando historial...
      </div>
    } @else if (events().length === 0) {
      <p class="py-2 text-xs text-slate-500">Sin eventos registrados.</p>
    } @else {
      <ol class="relative ml-2 border-l border-slate-200">
        @for (event of events(); track event.id) {
          <li class="ml-4 pb-3 last:pb-0">
            <span class="absolute -left-2.5 flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-white"
                  [ngClass]="dotClass(event)">
              <i class="pi text-[10px]" [ngClass]="icon(event)"></i>
            </span>
            <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span class="text-xs font-medium text-slate-900">{{ eventLabel(event) }}</span>
              @if (event.toStatus && event.toStatus !== event.fromStatus) {
                <span class="text-[11px] text-slate-500">
                  @if (event.fromStatus) { {{ statusLabel(event.fromStatus) }} → }
                  <span class="font-medium text-slate-700">{{ statusLabel(event.toStatus) }}</span>
                </span>
              }
            </div>
            <div class="flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500">
              <span>{{ event.occurredAt | date:'dd/MM/yyyy HH:mm:ss' }}</span>
              <span>·</span>
              <span>{{ actorLabel(event) }}</span>
            </div>
            @if (event.providerCode || event.providerMessage) {
              <p class="mt-0.5 text-[11px] text-slate-600">
                @if (event.providerCode) { <span class="font-mono">{{ event.providerCode }}</span> }
                @if (event.providerCode && event.providerMessage) { · }
                {{ event.providerMessage }}
              </p>
            }
            @for (line of detailLines(event); track $index) {
              <p class="mt-0.5 text-[11px] text-slate-500">{{ line }}</p>
            }
          </li>
        }
      </ol>
    }
  `
})
export class CollectionAttemptTimelineComponent {
  events = input<CollectionAttemptEventDTO[]>([]);
  loading = input(false);

  eventLabel(event: CollectionAttemptEventDTO): string {
    return EVENT_LABELS[event.eventType] ?? event.eventType;
  }

  statusLabel(status: CollectionAttemptStatusValue): string {
    return STATUS_LABELS[status] ?? status;
  }

  actorLabel(event: CollectionAttemptEventDTO): string {
    if (event.actorName) return event.actorName;
    return ACTOR_LABELS[event.actorType] ?? event.actorType;
  }

  icon(event: CollectionAttemptEventDTO): string {
    return EVENT_ICONS[event.eventType] ?? 'pi-circle';
  }

  dotClass(event: CollectionAttemptEventDTO): string {
    switch (event.toStatus) {
      case 'APPROVED':
        return 'bg-emerald-100 text-emerald-700';
      case 'DECLINED':
      case 'REJECTED':
      case 'VOID_FAILED':
        return 'bg-red-100 text-red-700';
      case 'PENDING_RECONCILIATION':
      case 'VOID_PENDING':
        return 'bg-amber-100 text-amber-700';
      case 'PROCESSING':
      case 'PENDING':
        return 'bg-sky-100 text-sky-700';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  }

  /** Resumen legible de `details`: valores anteriores, resultado de una resolución o error de materialización. */
  detailLines(event: CollectionAttemptEventDTO): string[] {
    const details = event.details ?? {};
    const lines: string[] = [];
    const previous = details['previous'] as Record<string, unknown> | undefined;
    if (previous) {
      const parts: string[] = [];
      if (previous['providerCode']) parts.push(`referencia ${previous['providerCode']}`);
      if (previous['providerMessage']) parts.push(`descripción "${previous['providerMessage']}"`);
      if (previous['attemptedAt']) {
        parts.push(`fecha ${new Date(String(previous['attemptedAt'])).toLocaleString('es-MX')}`);
      }
      if (parts.length) lines.push(`Antes: ${parts.join(', ')}`);
    }
    if (details['outcome']) lines.push(`Resultado: ${this.outcomeLabel(String(details['outcome']))}`);
    if (details['note']) lines.push(`Nota: ${details['note']}`);
    if (details['error']) lines.push(`Error: ${details['error']}`);
    return lines;
  }

  private outcomeLabel(outcome: string): string {
    const labels: Record<string, string> = { APPROVED: 'Aprobado', DECLINED: 'Declinado', VOIDED: 'Cancelado' };
    return labels[outcome] ?? outcome;
  }
}
